// src/admin/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Home, Building2, Users, DollarSign } from 'lucide-react';
import { api } from '../../services/api';
import { authService } from '../../services/auth';

const Dashboard = () => {
  const [stats, setStats] = useState([
    { label: 'Total Propiedades', value: '0', icon: Building2, color: 'bg-blue-500' },
    { label: 'Propiedades Activas', value: '0', icon: Home, color: 'bg-green-500' },
    { label: 'En Alquiler', value: '0', icon: DollarSign, color: 'bg-yellow-500' },
    { label: 'Usuarios Activos', value: '0', icon: Users, color: 'bg-purple-500' },
  ]);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // 1. Cargar propiedades
      const properties = await api.getProperties();
      const activeProperties = properties.filter(p => 
        p.status === 'Disponible' || p.status === 'Venta' || p.status === 'Alquiler'
      );
      
      const rentalProperties = properties.filter(p => 
        p.category === 'alquiler' || p.status === 'Alquiler'
      );

      // 2. Cargar usuarios (solo si es admin)
      const currentUser = authService.getUser();
      let activeUsers = 0;
      
      if (currentUser?.role === 'admin') {
        // Intentar cargar usuarios si el endpoint existe
        try {
          const usersResult = await authService.getUsers();
          if (usersResult.success) {
            activeUsers = usersResult.users.filter(u => u.active).length;
          }
        } catch (error) {
          console.log('No se pudo cargar usuarios:', error.message);
          // Si falla, usar un valor por defecto
          activeUsers = 1; // Al menos el usuario actual
        }
      } else {
        activeUsers = 1; // Solo el usuario actual
      }

      // 3. Actualizar estadísticas
      setStats([
        { 
          label: 'Total Propiedades', 
          value: properties.length.toString(), 
          icon: Building2, 
          color: 'bg-blue-500' 
        },
        { 
          label: 'Propiedades Activas', 
          value: activeProperties.length.toString(), 
          icon: Home, 
          color: 'bg-green-500' 
        },
        { 
          label: 'En Alquiler', 
          value: rentalProperties.length.toString(), 
          icon: DollarSign, 
          color: 'bg-yellow-500' 
        },
        { 
          label: 'Usuarios Activos', 
          value: activeUsers.toString(), 
          icon: Users, 
          color: 'bg-purple-500' 
        },
      ]);

      // 4. Crear actividad reciente simulada
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const mockActivity = [];
      
      // Actividad de propiedades recientes
      const recentProperties = properties.slice(0, 3);
      recentProperties.forEach(prop => {
        mockActivity.push({
          type: 'property',
          title: 'Propiedad agregada',
          description: `${prop.title}`,
          time: 'Hoy 10:30 AM',
          icon: Building2,
          color: 'bg-blue-100',
          iconColor: 'text-blue-600'
        });
      });

      // Agregar actividad de usuarios si hay más de 1
      if (activeUsers > 1) {
        mockActivity.push({
          type: 'user',
          title: 'Usuario registrado',
          description: 'Nuevo agente en el sistema',
          time: 'Ayer 3:45 PM',
          icon: Users,
          color: 'bg-green-100',
          iconColor: 'text-green-600'
        });
      }

      setRecentActivity(mockActivity);

    } catch (error) {
      console.error('Error cargando datos del dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuickActions = () => {
    const currentUser = authService.getUser();
    const isAdmin = currentUser?.role === 'admin';
    
    return [
      {
        title: 'Agregar Propiedad',
        description: 'Crear nueva propiedad',
        icon: '➕',
        href: '/admin/properties/new',
        color: 'hover:border-blue-500',
        iconColor: 'text-blue-500'
      },
      {
        title: 'Ver Propiedades',
        description: 'Gestionar propiedades existentes',
        icon: '📋',
        href: '/admin/properties',
        color: 'hover:border-green-500',
        iconColor: 'text-green-500'
      },
      {
        title: isAdmin ? 'Gestionar Usuarios' : 'Mi Perfil',
        description: isAdmin ? 'Administrar usuarios del sistema' : 'Ver y editar mi perfil',
        icon: isAdmin ? '👥' : '👤',
        href: isAdmin ? '/admin/users' : '#',
        color: 'hover:border-purple-500',
        iconColor: 'text-purple-500',
        onClick: !isAdmin ? () => alert('Perfil en desarrollo') : undefined
      }
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600">Bienvenido al panel de administración</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800 mt-2">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-full`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getQuickActions().map((action, index) => (
            <a
              key={index}
              href={action.href}
              onClick={action.onClick}
              className={`border-2 border-dashed border-gray-300 ${action.color} rounded-lg p-6 text-center transition-all hover:scale-[1.02]`}
            >
              <div className={`text-2xl mb-2 ${action.iconColor}`}>{action.icon}</div>
              <p className="font-medium text-gray-800">{action.title}</p>
              <p className="text-sm text-gray-500 mt-1">{action.description}</p>
            </a>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Actividad Reciente</h2>
        
        {recentActivity.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-600">No hay actividad reciente</p>
            <p className="text-sm text-gray-500 mt-1">Las actividades aparecerán aquí</p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentActivity.map((activity, index) => {
              const Icon = activity.icon;
              return (
                <div key={index} className="flex items-center p-4 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className={`w-10 h-10 ${activity.color} rounded-full flex items-center justify-center mr-4`}>
                    <Icon className={`w-5 h-5 ${activity.iconColor}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{activity.title}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                  </div>
                  <div className="text-sm text-gray-500">{activity.time}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Información del sistema */}
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">ℹ️ Información del Sistema</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">Estado del servidor:</p>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              <span className="text-green-700 font-medium">En línea</span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Última actualización:</p>
            <span className="text-gray-800">{new Date().toLocaleDateString('es-ES')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;